import { useEffect, useId, useRef, useState } from "react";

import { useAuth } from "../auth/AuthContext.js";
import { accountName } from "../lib/accounts.js";
import { api, ApiError, signatureLogoUrl } from "../lib/api.js";
import { disablePush, enablePush, pushState, type PushState } from "../lib/push.js";
import { LOGO_SRC, TEMPLATES, withDemoFallback } from "../lib/signatureTemplates.js";
import type { AccountInfo, SignatureFields, SignatureSettings } from "../lib/types.js";

/**
 * Instellingen: meldingen, wachtwoord en handtekening.
 *
 * De handtekening werkt hier van boven naar beneden: eerst uw gegevens, dan de
 * vorm. Dat is bewust andersom dan een HTML-veld met een voorbeeldknop — vrijwel
 * niemand schrijft met plezier HTML, en wie dat wél doet vindt het veld nog
 * steeds, onder "Zelf HTML aanpassen".
 *
 * De HTML gaat serverzijde door dezelfde sanitizer als inkomende mail. Wat je
 * hier terugkrijgt na opslaan is dus wát er echt bewaard is — als er iets uit
 * gestript is, zie je dat meteen in het voorbeeld in plaats van pas bij de
 * ontvanger.
 */

interface Props {
  onClose: () => void;
}

interface AccountSettingsProps {
  account: AccountInfo;
  accounts: AccountInfo[];
  onAccountChange: (id: string) => void;
  onClose: () => void;
}

/** De kleuren waaruit de sjablonen hun accent halen; vrij kiezen kan met de kiezer ernaast. */
const ACCENTEN = ["#fe7b05", "#1f6feb", "#128a5b", "#b3261e", "#6b4ee6", "#212529"];

const VELDEN: { key: keyof SignatureFields; label: string; placeholder: string; type?: string }[] = [
  { key: "naam", label: "Naam", placeholder: "Richard van der Gulik" },
  { key: "functie", label: "Functie", placeholder: "Eigenaar" },
  { key: "bedrijf", label: "Bedrijf", placeholder: "Rulido" },
  { key: "telefoon", label: "Telefoon", placeholder: "010 - 123 45 67", type: "tel" },
  { key: "mobiel", label: "Mobiel", placeholder: "+31 6 59 10 43 32", type: "tel" },
  { key: "email", label: "E-mail", placeholder: "naam@uwdomein.nl", type: "email" },
  { key: "website", label: "Website", placeholder: "uwdomein.nl" },
];

function buildHtml(template: string, fields: SignatureFields, logo: string | null): string {
  const tpl = TEMPLATES.find((t) => t.id === template);
  return tpl === undefined ? "" : tpl.build(fields, logo);
}

/**
 * In de opgeslagen handtekening wijst het logo naar een pad zonder account
 * (bij verzenden wordt het een inline bijlage). Om hier het logo van DIT
 * account te tonen en niet dat van de eerste mailbox, krijgt het voorbeeld de
 * URL mét account.
 */
function previewHtml(html: string, logoUrl: string | null): string {
  if (logoUrl === null) return html;
  return html.split(`"${LOGO_SRC}"`).join(`"${logoUrl}"`);
}

/**
 * Met meerdere mailboxen geldt alles hieronder (behalve meldingen) per
 * mailbox: elk bedrijf zijn eigen handtekening en zijn eigen wachtwoord. Het
 * binnenste deel krijgt per mailbox een eigen key, zodat er nooit gegevens van
 * de ene in het formulier van de andere blijven hangen.
 */
export function Settings({ onClose }: Props): React.JSX.Element {
  const { accounts, active } = useAuth();
  const [accountId, setAccountId] = useState(active?.id ?? accounts[0]?.id ?? "");
  const account = accounts.find((a) => a.id === accountId) ?? active ?? accounts[0];
  if (account === undefined || account === null) return <></>;
  return (
    <AccountSettings
      key={account.id}
      account={account}
      accounts={accounts}
      onAccountChange={setAccountId}
      onClose={onClose}
    />
  );
}

function AccountSettings({ account, accounts, onAccountChange, onClose }: AccountSettingsProps): React.JSX.Element {
  const accountSelectId = useId();
  const multiple = accounts.length > 1;

  const [sig, setSig] = useState<SignatureSettings | null>(null);
  const [html, setHtml] = useState("");
  const [fields, setFields] = useState<SignatureFields | null>(null);
  const [template, setTemplate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [toonHtml, setToonHtml] = useState(false);
  /**
   * Er zijn wijzigingen die de HTML niet raken — gegevens invullen vóórdat er
   * een vorm gekozen is, bijvoorbeeld. Een vergelijking met de opgeslagen HTML
   * zou die stilletjes laten vallen, dus houden we het apart bij.
   */
  const [vuil, setVuil] = useState(false);
  /**
   * Telt elke wijziging. Opslaan gebeurt bij het verlaten van een veld; wie
   * dan meteen in het volgende veld verder typt, mag dat niet kwijtraken als
   * het antwoord van de server binnenkomt.
   */
  const edits = useRef(0);

  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);

  const [push, setPush] = useState<PushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNext, setPwNext] = useState("");
  const [pwRepeat, setPwRepeat] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMessage, setPwMessage] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  async function changePassword(): Promise<void> {
    setPwError(null);
    setPwMessage(null);
    if (pwNext.length < 10) {
      setPwError("Het nieuwe wachtwoord moet minstens 10 tekens zijn.");
      return;
    }
    if (pwNext !== pwRepeat) {
      setPwError("De herhaling komt niet overeen.");
      return;
    }
    setPwBusy(true);
    try {
      const result = await api.changePassword(pwCurrent, pwNext, account.id);
      setPwCurrent(""); setPwNext(""); setPwRepeat("");
      setPwMessage(
        result.otherSessionsEnded > 0
          ? `Gewijzigd. ${result.otherSessionsEnded} ander(e) apparaat(en) zijn uitgelogd — log daar opnieuw in met het nieuwe wachtwoord.`
          : "Gewijzigd. Gebruik het nieuwe wachtwoord voortaan ook op uw telefoon en in het klantportaal.",
      );
    } catch (cause) {
      setPwError(cause instanceof ApiError ? cause.message : "Wijzigen mislukt.");
    } finally {
      setPwBusy(false);
    }
  }

  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  useEffect(() => {
    api
      .signature(account.id)
      .then((s) => {
        setSig(s);
        setHtml(s.html);
        setTemplate(s.template);
        // Het eigen adres alvast invullen: dat is het enige veld waarvan we het
        // antwoord al weten, en het scheelt de eerste tikbeurt.
        setFields({ ...s.fields, email: s.fields.email || account.address });
        setToonHtml(s.template === "" && s.html !== "");
      })
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "Laden mislukt."),
      );
  }, [account.id, account.address]);

  /**
   * Eén plek waar opslaan gebeurt. De aanroeper geeft mee wát er verandert;
   * alles wat hij niet noemt komt uit de huidige staat. Zonder dat zou een
   * klik op een sjabloon de nog niet doorgegeven React-staat opslaan en dus
   * het vorige sjabloon bewaren.
   */
  async function save(next: Partial<SignatureSettings> = {}): Promise<void> {
    if (fields === null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    const startedAt = edits.current;
    try {
      const result = await api.saveSignature({ html, template, fields, ...next }, account.id);
      setSig(result);
      // Intussen verder getypt? Dan gaat die invoer voor; hij blijft "vuil" en
      // wordt bij het verlaten van dat veld alsnog opgeslagen.
      if (edits.current !== startedAt) return;
      // De server geeft de gesaneerde versie terug; die tonen we, zodat een
      // gestript stuk niet stilletjes verdwijnt.
      setHtml(result.html);
      setTemplate(result.template);
      setFields(result.fields);
      setVuil(false);
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  }

  const heeftLogo = sig?.logo != null;

  /** Past een sjabloon toe en bewaart het meteen, zodat het voorbeeld klopt. */
  function kiesSjabloon(id: string, volgende?: SignatureFields): void {
    const f = volgende ?? fields;
    if (f === null) return;
    const nieuweHtml = buildHtml(id, f, heeftLogo ? LOGO_SRC : null);
    setTemplate(id);
    setHtml(nieuweHtml);
    void save({ template: id, html: nieuweHtml, fields: f });
  }

  /** Tikken in een veld werkt de HTML live bij; opslaan gebeurt zodra het veld de focus verliest. */
  function wijzigVeld(key: keyof SignatureFields, waarde: string): void {
    if (fields === null) return;
    edits.current += 1;
    const volgende = { ...fields, [key]: waarde };
    setFields(volgende);
    setSaved(false);
    setVuil(true);
    if (template !== "") setHtml(buildHtml(template, volgende, heeftLogo ? LOGO_SRC : null));
  }

  async function uploadLogo(file: File): Promise<void> {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const result = await api.uploadSignatureLogo(file, account.id);
      setSig(result);
      // Het sjabloon moet opnieuw getekend worden: er is nu een <img> bij.
      if (template !== "" && fields !== null) {
        const nieuweHtml = buildHtml(template, fields, LOGO_SRC);
        setHtml(nieuweHtml);
        const opgeslagen = await api.saveSignature({ html: nieuweHtml, template, fields }, account.id);
        setSig(opgeslagen);
        setHtml(opgeslagen.html);
      }
    } catch (cause) {
      setLogoError(cause instanceof ApiError ? cause.message : "Uploaden mislukt.");
    } finally {
      setLogoBusy(false);
      if (logoInput.current !== null) logoInput.current.value = "";
    }
  }

  async function verwijderLogo(): Promise<void> {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const result = await api.removeSignatureLogo(account.id);
      setSig(result);
      setHtml(result.html);
      if (template !== "" && fields !== null) {
        const nieuweHtml = buildHtml(template, fields, null);
        setHtml(nieuweHtml);
        const opgeslagen = await api.saveSignature({ html: nieuweHtml, template, fields }, account.id);
        setSig(opgeslagen);
        setHtml(opgeslagen.html);
      }
    } catch (cause) {
      setLogoError(cause instanceof ApiError ? cause.message : "Verwijderen mislukt.");
    } finally {
      setLogoBusy(false);
    }
  }

  /**
   * Rulido-klanten krijgen het huisstijllogo met één klik; het staat toch al in
   * de app. Voor iedereen anders is de uploadknop de weg — vandaar dat dit
   * alleen verschijnt bij een @rulido.nl-adres.
   */
  const isRulido = account.address.toLowerCase().endsWith("@rulido.nl");

  async function gebruikRulidoLogo(): Promise<void> {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const response = await fetch("/rulido-logo.png");
      if (!response.ok) throw new Error("niet gevonden");
      const blob = await response.blob();
      await uploadLogo(new File([blob], "rulido-logo.png", { type: "image/png" }));
    } catch {
      setLogoError("Het Rulido-logo kon niet geladen worden.");
      setLogoBusy(false);
    }
  }

  const logoUrl = sig?.logo != null ? signatureLogoUrl(sig.logo.updatedAt, account.id) : null;
  const nietOpgeslagen = sig !== null && html !== sig.html;

  return (
    <div className="composer-backdrop" role="dialog" aria-modal="true" aria-label="Instellingen">
      <div className="composer settings">
        <header className="composer-head">
          <h2>Instellingen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten">×</button>
        </header>

        {/*
          Eigen scrollgebied tussen kop en knoppenbalk. Zonder dit duwde de
          inhoud de balk met Opslaan op een desktopscherm buiten het venster:
          de dialoog heeft een max-height, maar niets ving het overschot op.
        */}
        <div className="settings-body">
          {multiple && (
            <section className="settings-section settings-account">
              <label className="settings-h3" htmlFor={accountSelectId}>Instellingen voor</label>
              <select
                id={accountSelectId}
                className="settings-account-select"
                value={account.id}
                onChange={(e) => onAccountChange(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {accountName(a.address)} — {a.address}
                  </option>
                ))}
              </select>
              <p className="settings-hint">
                Handtekening en wachtwoord gelden per mailbox. Een antwoord vanuit{" "}
                {accountName(account.address)} krijgt de handtekening die u hier voor{" "}
                {account.address} instelt.
              </p>
            </section>
          )}

          <section className="settings-section">
            <h3 className="settings-h3">Meldingen bij nieuwe post</h3>
            {push === "enabled" && (
              <>
                <p className="settings-hint settings-hint--ok">
                  {multiple
                    ? "Meldingen staan aan op dit apparaat, voor al uw mailboxen."
                    : "Meldingen staan aan op dit apparaat."}
                </p>
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={pushBusy}
                  onClick={() => {
                    setPushBusy(true);
                    void disablePush().then(setPush).finally(() => setPushBusy(false));
                  }}
                >
                  Uitzetten
                </button>
              </>
            )}
            {push === "disabled" && (
              <>
                <p className="settings-hint">
                  U krijgt een melding zodra er post binnenkomt. In de melding staat
                  alleen dát er post is — afzender en onderwerp blijven privé.
                </p>
                <button
                  type="button"
                  className="btn btn--send"
                  disabled={pushBusy}
                  onClick={() => {
                    setPushBusy(true);
                    void enablePush().then(setPush).finally(() => setPushBusy(false));
                  }}
                >
                  {pushBusy ? "Bezig…" : "Meldingen aanzetten"}
                </button>
              </>
            )}
            {push === "ios-needs-install" && (
              <p className="settings-hint">
                Op de iPhone en iPad werken meldingen alleen als de webmail op uw
                beginscherm staat. Tik onderin op <strong>Deel</strong> en daarna op
                <strong> Zet op beginscherm</strong>; open de app vervolgens vanaf dat
                icoon en zet de meldingen hier aan.
              </p>
            )}
            {push === "denied" && (
              <p className="settings-hint">
                U heeft meldingen eerder geweigerd. Dat kan alleen in de instellingen
                van uw browser worden teruggedraaid, niet vanuit deze pagina.
              </p>
            )}
            {push === "unsupported" && (
              <p className="settings-hint">
                Deze browser ondersteunt geen meldingen.
              </p>
            )}
          </section>

          <section className="settings-section">
            <h3 className="settings-h3">
              {multiple ? `Wachtwoord van ${account.address}` : "Wachtwoord wijzigen"}
            </h3>
            <p className="settings-hint">
              Uw wachtwoord geldt overal tegelijk: webmail, telefoon, Outlook én het
              klantportaal. Na het wijzigen worden andere apparaten uitgelogd.
            </p>
            <div className="pw-grid">
              <input type="password" className="pw-input" placeholder="Huidig wachtwoord"
                autoComplete="current-password" value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)} disabled={pwBusy} />
              <input type="password" className="pw-input" placeholder="Nieuw wachtwoord (min. 10 tekens)"
                autoComplete="new-password" value={pwNext}
                onChange={(e) => setPwNext(e.target.value)} disabled={pwBusy} />
              <input type="password" className="pw-input" placeholder="Nieuw wachtwoord herhalen"
                autoComplete="new-password" value={pwRepeat}
                onChange={(e) => setPwRepeat(e.target.value)} disabled={pwBusy} />
            </div>
            {pwError !== null && <p className="composer-error" role="alert">{pwError}</p>}
            {pwMessage !== null && <p className="settings-hint settings-hint--ok">{pwMessage}</p>}
            <button type="button" className="btn btn--quiet" disabled={pwBusy || pwCurrent === ""}
              onClick={() => void changePassword()}>
              {pwBusy ? "Bezig…" : "Wachtwoord wijzigen"}
            </button>
          </section>

          {sig === null && error === null && <p className="settings-hint">Laden…</p>}

          {sig !== null && fields !== null && (
            <>
              <section className="settings-section">
                <h3 className="settings-h3">
                  {multiple ? `Handtekening van ${accountName(account.address)} — uw gegevens` : "Handtekening — uw gegevens"}
                </h3>
                <p className="settings-hint">
                  Vul in wat onder uw berichten moet komen. Lege velden worden
                  gewoon weggelaten, dus u hoeft niet alles in te vullen.
                </p>

                <div className="sig-fields">
                  {VELDEN.map((veld) => (
                    <label className="sig-field" key={veld.key}>
                      <span>{veld.label}</span>
                      <input
                        type={veld.type ?? "text"}
                        value={fields[veld.key]}
                        placeholder={veld.placeholder}
                        autoComplete="off"
                        onChange={(e) => wijzigVeld(veld.key, e.target.value)}
                        onBlur={() => { if (vuil) void save(); }}
                      />
                    </label>
                  ))}
                  <label className="sig-field sig-field--wide">
                    <span>Adres</span>
                    <textarea
                      rows={2}
                      value={fields.adres}
                      placeholder="Straatnaam 1, 3011 AA Rotterdam"
                      onChange={(e) => wijzigVeld("adres", e.target.value)}
                      onBlur={() => { if (vuil) void save(); }}
                    />
                  </label>
                </div>

                <div className="sig-accent">
                  <span className="sig-accent-label">Accentkleur</span>
                  {ACCENTEN.map((kleur) => (
                    <button
                      key={kleur}
                      type="button"
                      className={`sig-swatch${fields.accent === kleur ? " is-active" : ""}`}
                      style={{ background: kleur }}
                      aria-label={`Accentkleur ${kleur}`}
                      aria-pressed={fields.accent === kleur}
                      onClick={() => {
                        const volgende = { ...fields, accent: kleur };
                        setFields(volgende);
                        if (template !== "") kiesSjabloon(template, volgende);
                        else void save({ fields: volgende });
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    className="sig-swatch sig-swatch--custom"
                    value={fields.accent}
                    aria-label="Eigen accentkleur"
                    onChange={(e) => wijzigVeld("accent", e.target.value)}
                    onBlur={() => { if (template !== "") kiesSjabloon(template); else void save(); }}
                  />
                </div>
              </section>

              <section className="settings-section">
                <h3 className="settings-h3">Logo</h3>
                <p className="settings-hint">
                  Het logo gaat als onderdeel van het bericht mee, niet als losse
                  bijlage en niet vanaf een website — de ontvanger ziet het dus
                  meteen. PNG, JPG, GIF of WebP, maximaal 200 KB.
                </p>

                <div className="sig-logo">
                  {logoUrl !== null && (
                    <img className="sig-logo-preview" src={logoUrl} alt="Uw logo" />
                  )}
                  <div className="sig-logo-actions">
                    <input
                      ref={logoInput}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="sig-logo-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file !== undefined) void uploadLogo(file);
                      }}
                    />
                    <button type="button" className="btn btn--quiet" disabled={logoBusy}
                      onClick={() => logoInput.current?.click()}>
                      {logoBusy ? "Bezig…" : logoUrl !== null ? "Ander logo kiezen" : "Logo uploaden"}
                    </button>
                    {logoUrl !== null && (
                      <button type="button" className="btn btn--text" disabled={logoBusy}
                        onClick={() => void verwijderLogo()}>
                        Verwijderen
                      </button>
                    )}
                    {logoUrl === null && isRulido && (
                      <button type="button" className="btn btn--text" disabled={logoBusy}
                        onClick={() => void gebruikRulidoLogo()}>
                        Rulido-logo gebruiken
                      </button>
                    )}
                  </div>
                </div>
                {logoError !== null && <p className="composer-error" role="alert">{logoError}</p>}
              </section>

              <section className="settings-section">
                <h3 className="settings-h3">Kies een vorm</h3>
                <p className="settings-hint">
                  Klik op een voorbeeld; uw gegevens worden er meteen in gezet en
                  opgeslagen. Wisselen kan altijd — uw gegevens blijven staan.
                </p>

                <div className="sig-templates">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      className={`sig-template${template === tpl.id ? " is-active" : ""}`}
                      aria-pressed={template === tpl.id}
                      onClick={() => kiesSjabloon(tpl.id)}
                    >
                      <span className="sig-template-canvas">
                        {/* Veilig: deze HTML bouwen we hier zelf op uit de velden,
                            die bij het samenstellen geëscaped worden. Er komt niets
                            in wat de gebruiker als opmaak heeft ingetikt. */}
                        <span
                          className="sig-template-render"
                          dangerouslySetInnerHTML={{
                            __html: tpl.build(withDemoFallback(fields), logoUrl),
                          }}
                        />
                      </span>
                      <span className="sig-template-naam">{tpl.naam}</span>
                      <span className="sig-template-uitleg">{tpl.omschrijving}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="settings-section">
                <h3 className="settings-h3">Wanneer plaatsen</h3>
                <div className="settings-toggles">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={sig.enabled}
                      onChange={(e) => void save({ enabled: e.target.checked })}
                    />
                    <span>Handtekening onder mijn berichten plaatsen</span>
                  </label>
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={sig.onReplies}
                      disabled={!sig.enabled}
                      onChange={(e) => void save({ onReplies: e.target.checked })}
                    />
                    <span>Ook onder antwoorden en doorstuurberichten</span>
                  </label>
                </div>
              </section>

              <section className="settings-section">
                <h3 className="settings-h3">Voorbeeld</h3>
                <div className="settings-preview">
                  <span className="settings-preview-label">
                    Zo ziet de ontvanger het
                    {nietOpgeslagen && <em className="sig-stale"> — nog niet opgeslagen</em>}
                  </span>
                  {/* Veilig: deze HTML is serverzijde gesaneerd en komt van de
                      gebruiker zelf. Scripts, iframes en externe stijlen zijn er al
                      uit gehaald voordat hij hier terechtkomt. */}
                  <div
                    className="settings-preview-body"
                    dangerouslySetInnerHTML={{ __html: previewHtml(sig.html, logoUrl) }}
                  />
                  {sig.html === "" && (
                    <p className="settings-hint" style={{ margin: 0 }}>
                      Nog geen handtekening. Kies hierboven een vorm.
                    </p>
                  )}
                </div>
              </section>

              <section className="settings-section">
                <button
                  type="button"
                  className="link-button sig-toggle-html"
                  aria-expanded={toonHtml}
                  onClick={() => setToonHtml((v) => !v)}
                >
                  {toonHtml ? "▾" : "▸"} Zelf HTML aanpassen
                </button>

                {toonHtml && (
                  <>
                    <p className="settings-hint">
                      Wat u hier wijzigt blijft staan tot u opnieuw een vorm kiest —
                      dan wordt het overschreven. Scripts, formulieren en externe
                      stijlen worden automatisch verwijderd; houd het eenvoudig, want
                      veel mailclients negeren de rest toch.
                      {heeftLogo && <> Uw logo hoort hier als <code>{`<img src="${LOGO_SRC}">`}</code>.</>}
                    </p>
                    <textarea
                      id="sig-html"
                      className="composer-body settings-code"
                      value={html}
                      onChange={(e) => {
                        edits.current += 1;
                        setHtml(e.target.value);
                        setTemplate("");
                        setSaved(false);
                        setVuil(true);
                      }}
                      onBlur={() => { if (vuil) void save({ template: "" }); }}
                      spellCheck={false}
                      placeholder="<p>Uw Naam<br>Bedrijf</p>"
                    />
                  </>
                )}
              </section>
            </>
          )}

          {error !== null && <p className="composer-error" role="alert">{error}</p>}
        </div>

        <footer className="composer-actions">
          <button type="button" className="btn btn--send" onClick={() => void save()} disabled={busy || sig === null}>
            {busy ? "Bezig…" : saved && !vuil ? "Opgeslagen" : "Opslaan"}
          </button>
          <button type="button" className="btn btn--text composer-draft" onClick={onClose}>
            Sluiten
          </button>
        </footer>
      </div>
    </div>
  );
}
